import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BasilNotification } from "@/components/icons/basil";
import { useProfile } from "@/components/layout/useProfile";
import {
  MOCK_NOTIFICATIONS,
  markAllRead,
  relativeLabel,
  unreadCount,
} from "@/lib/renewal/notifications";
import { cn } from "@shared/lib/utils";

/** 알림 유형 → 눈썹 라벨 (색=의미 사전: 알림은 전부 중립, 뱃지 남발 금지 §1.3) */
const KIND_LABEL: Record<string, string> = {
  mission: "Mission",
  pull: "Pull",
  season: "Season",
  system: "System",
};

/**
 * 상단 헤더 알림 센터 — 벨 버튼(미읽음 레드닷) + 드롭다운 패널.
 * ProfileChip 드롭다운과 동일 문법(ty-profile-pop, 외부 클릭·ESC 닫힘).
 * 열람 시 전체 읽음 처리. TODO(server): notifications RPC 연결.
 */
export function NotificationCenter() {
  const { authenticated } = useProfile();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(() => unreadCount());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    markAllRead();
    setUnread(0);
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // 알림은 계정 귀속 — 미로그인 시 벨 자체를 렌더하지 않는다.
  if (!authenticated) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : "Notifications"}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="relative grid size-11 place-items-center rounded-full text-ink-dim outline-none transition-colors duration-200 hover:text-ink focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60"
      >
        <BasilNotification className="size-[18px]" aria-hidden />
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute right-2.5 top-2.5 size-1.5 rounded-full bg-danger"
          />
        )}
      </button>

      {open ? (
        <div className="ty-profile-pop absolute right-0 top-full z-50 mt-2 w-80 rounded-2xl border border-line bg-(--ty-bg-2) p-1.5 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.85)]">
          <p className="ty-eyebrow px-3 pb-1.5 pt-2 text-[10px]">Notifications</p>
          {MOCK_NOTIFICATIONS.length === 0 ? (
            <p className="px-3 pb-3 text-xs text-ink-mute">No notifications yet.</p>
          ) : (
            <ul className="flex flex-col">
              {MOCK_NOTIFICATIONS.map((n) => (
                <li key={n.id}>
                  <Link
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "flex min-h-11 flex-col justify-center gap-0.5 rounded-lg px-3 py-2 outline-none transition-colors duration-200",
                      "hover:bg-white/5 focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-[9px] font-medium uppercase tracking-[0.12em] text-accent">
                        {KIND_LABEL[n.kind]}
                      </span>
                      <span className="font-mono text-[10px] tabular-nums text-ink-mute">
                        {relativeLabel(n.minutesAgo)}
                      </span>
                    </span>
                    <span className="truncate text-xs text-ink">{n.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
